import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { OrderPaymentCardCreation } from '../models/order-payment-card';
import { Package } from '../models/package';
import { currencyUtils } from '../utils/currency';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { computed } from 'vue';

const initialNewOrder = {
  selectPackage: null,
  productsIncluded: [],
  quantity: 1,
  discount: null,
  price: 0,
  totalPrice: 0,
  discountCode: null,
  discountAmountPerUnitWhenApplyCode: 0,
};

const initialNewPackagesOrder = {
  /** @type {null | Package} */
  selectPackage: null,
  quantity: 1,
  price: 0,
  totalPrice: 0,
};

const actionButtonTypeOrder = {
  addOrChangeCreditCard: globalTranslate('ordersService.addOrChangeCreditCard'),
  payNow: globalTranslate('ordersService.payNow'),
};

/**
 * Map to card creation data from payment form (use for paying order with new card).
 * @param {object} data Card information
 * @returns {OrderPaymentCardCreation}
 */
function mapToCardCreationFromPaymentForm(data) {
  return {
    paymentProfileId: data.paymentProfileId ?? null,
    card: {
      type: data.card.value,
      token: data.token,
      billingAddress: data.billingAddress,
      billingZip: data.billingZip,
      isSaveCard: data.shouldSaveForLaterUse,
      name: data.name,
    },
  };
}

/**
 * Calculate discount price for package.
 * @param {object} param Package information
 * @param {object} param.selectedPackage Package to calculate.
 * @param {number} param.quantity Quantity of package.
 * @returns {number}
 */
function calculateDiscountForPackage({ selectedPackage, quantity }) {
  let discountPrice = 0;

  selectedPackage.packageDiscounts.forEach(p => {
    if (quantity > p.cutoffCount) {
      discountPrice = p.amount;
    }
  });
  return discountPrice;
}

/**
 * To readable total discount amount.
 * @param {object} root0 Params.
 * @param {number} root0.perUnitAmount Per unit amount.
 * @param {number} root0.quantity Quantity.
 * @returns {string}
 */
function toReadableTotalDiscountAmount({ perUnitAmount, quantity }) {
  const totalDiscountAmount = perUnitAmount * quantity;
  return `${currencyUtils.toUSCurrency(totalDiscountAmount)} (${currencyUtils.toUSCurrency(perUnitAmount)} x ${quantity})`;
}

/**
 * Calculate total price.
 * @param {object} orderInfo Selected package.
 * @param {boolean} isBeforeDiscount Whether price is before discount or not.
 * @returns {number}
 */
function calculateTotalPrice(orderInfo, isBeforeDiscount = false) {
  const {
    selectPackage, quantity, discount, discountAmountPerUnitWhenApplyCode = 0,
  } = orderInfo;
  if (selectPackage == null) {
    return 0;
  }
  // Since we are using `reduce` method, we need to assign accumulative variable directly so these rules are disabled.
  const packagePrice = selectPackage.includedProducts.reduce(
    // eslint-disable-next-line no-return-assign, no-param-reassign
    (acc, cur) => (acc += cur.unitPrice),
    0,
  );

  const discountPrice = calculateDiscountForPackage({ selectedPackage: selectPackage, quantity });

  const discountCodeAmount = discountAmountPerUnitWhenApplyCode * quantity;
  const beforeDiscountPrice = (packagePrice - discountPrice) * quantity;
  const priceAfterDiscountCode = beforeDiscountPrice - discountCodeAmount;

  if (isBeforeDiscount) {
    return priceAfterDiscountCode;
  }
  return priceAfterDiscountCode - Number(discount ?? 0);
}

export const ordersTableHeaders = [
  {
    title: '',
    key: 'selection',
    sortable: false,
    align: 'center',
    width: '50px',
  },
  {
    title: globalTranslate("orders.order"),
    key: 'id',
    sortable: true,
    align: 'left',
  },
  {
    title: globalTranslate('orders.createdDate'),
    key: 'createdDate',
    sortable: false,
    align: 'center',
  },
  {
    title: globalTranslate('orders.orderTotal'),
    key: 'total',
    sortable: false,
    align: 'center',
  },
  {
    title: globalTranslate('orders.balance'),
    key: 'balance',
    sortable: false,
    align: 'center',
  },
  {
    title: globalTranslate('assignedCourseTable.quickActions'),
    key: 'quickActions',
    sortable: false,
    align: 'center',
  }
];


function getOrderDetailUrl (item) {
  const authStore = useAuthStore();
  const isPfsAdmin = computed(() => authStore.currentUser.isPfsAdmin);
  const isGroupAdmin = computed(() => authStore.currentUser.isGroupAdmin);
  var orderId, userId;
  const reg = item.value?.registration;
  if (reg){
    orderId = reg.orderItem?.order?.id;
    userId = reg.user?.managerId || reg.user?.id;
  } else {
    orderId = item?.value?.orderItem?.order?.id;
    userId = item?.value?.user?.managerId || item?.value?.user?.id || authStore.currentUser.id;
  }
  if (isPfsAdmin.value) {
    return "/pfs-admin/orders?userId=" + userId + (item?.value? "&orderId=" + orderId : '');
  }
  if (isGroupAdmin.value) {
    return "/group-admin/orders" + (item?.value? "?orderId=" + orderId : '');
  }
}

/**
 * Go to order payment from in-person 
 * registration with a balance
 * @param {Object} item
 */
function payNow(item) {
  var url = `/v2` + getOrderDetailUrl(item) + `&payNow=true`;
  window.location.href = url
}

/**
 * Go to order payment for a given order (e.g. unpaid orders on dashboard).
 * @param {number} orderId Order ID.
 */
function payNowForOrder(orderId) {
  payNow({ value: { orderItem: { order: { id: orderId } } } });
}

export const ordersService = {
  // Variables.
  initialNewOrder,
  actionButtonTypeOrder,
  initialNewPackagesOrder,
  ordersTableHeaders,
  // Functions.
  mapToCardCreationFromPaymentForm,
  calculateDiscountForPackage,
  calculateTotalPrice,
  toReadableTotalDiscountAmount,
  payNow,
  payNowForOrder,
  getOrderDetailUrl,
};
